import * as React from "react";
import {
  TabletSmartphone,
  Chrome,
  MonitorCheck,
  ChevronsLeftRightEllipsis,
  Database,
  UserRoundCheck,
  CheckIcon,
  LockKeyhole,
  Sparkles,
  HardDrive,
  Container,
  Activity,
  MessageSquareShare,
  DatabaseBackup,
  Monitor,
  Bot,
} from "lucide-react";
import {
  SiPython,
  SiGo,
  SiRust,
  SiBun,
  SiNextdotjs,
  SiAstro,
  SiReact,
  SiCloudflare,
  SiFlutter,
  SiSwift,
  SiKotlin,
  SiExpo,
  SiIonic,
  SiSvelte,
  SiVuedotjs,
  SiNuxtdotjs,
  SiAngular,
  SiSolid,
  SiQwik,
  SiReactquery,
  SiTauri,
  SiWails,
  SiNeutralinojs,
  SiQt,
  SiGtk,
  SiMysql,
  SiMongodb,
  SiRedis,
  SiSqlite,
  SiMariadb,
  SiSurrealdb,
  SiDuckdb,
  SiSupabase,
  SiGhost,
  SiHugo,
  SiJekyll,
  SiGatsby,
  SiDocusaurus,
  SiHashnode,
  SiWordpress,
  SiFramer,
  SiWebflow,
  SiCarrd,
  SiWix,
  SiSquarespace,
  SiTailwindcss,
  SiClerk,
  SiAuth0,
  SiFirebase,
  SiKeycloak,
  SiPocketbase,
  SiOkta,
  SiDiscord,
  SiX,
  SiFacebook,
  SiLinkedin,
  SiSlack,
  SiTwitch,
  SiBitbucket,
  SiGithub,
  SiGitlab,
  SiApple,
} from "react-icons/si";
import { RiNodejsLine } from "react-icons/ri";
import { BiLogoPostgresql } from "react-icons/bi";
import { FcGoogle } from "react-icons/fc";

const authConfig = {
  label: "Enable Auth",
  tooltip: "Will be validating if the app connects to an auth-service if checked",
  defaultValue: false,
  icon: <LockKeyhole className="w-4" />,
  iconLabel: "Auth Enabled",
};

const providerConfig = {
  label: "OAuth Providers",
  tooltip: "Choose the providers using which your users can login",
  defaultValue: "",
  options: [
    {
      value: "google",
      label: "Google",
      icon: <FcGoogle className="w-4" />,
    },
    {
      value: "github",
      label: "Github",
      icon: <SiGithub className="w-4 text-[#181717] dark:text-white" />,
    },
    {
      value: "gitlab",
      label: "Gitlab",
      icon: <SiGitlab className="w-4 text-[#FC6D26]" />,
    },
    {
      value: "bitbucket",
      label: "Bitbucket",
      icon: <SiBitbucket className="w-4 text-[#0052CC]" />,
    },
    {
      value: "apple",
      label: "Apple ID",
      icon: <SiApple className="w-4 text-[#000000] dark:text-white" />,
    },
    {
      value: "discord",
      label: "Discord",
      icon: <SiDiscord className="w-4 text-[#5865F2]" />,
      disabled: true,
    },
    {
      value: "x",
      label: "X",
      icon: <SiX className="w-4 text-[#000000] dark:text-white" />,
      disabled: true,
    },
    {
      value: "facebook",
      label: "Facebook",
      icon: <SiFacebook className="w-4 text-[#1877F2]" />,
      disabled: true,
    },
    {
      value: "linkedin",
      label: "LinkedIn",
      icon: <SiLinkedin className="w-4 text-[#0A66C2]" />,
      disabled: true,
    },
    {
      value: "slack",
      label: "Slack",
      icon: <SiSlack className="w-4 text-[#4A154B]" />,
      disabled: true,
    },
    {
      value: "twitch",
      label: "Twitch",
      icon: <SiTwitch className="w-4 text-[#9146FF]" />,
      disabled: true,
    },
  ],
  icon: <CheckIcon className="w-4" />,
  iconLabel: "OAuth Provider",
};

export const dbConfig = {
  label: "Enable DB Connection",
  tooltip: "Requires a connection to a database node",
  defaultValue: false,
  icon: <Database className="w-4" />,
  iconLabel: "DB Connection Enabled",
};

interface AppConfigItem {
  label: string;
  tooltip: string;
  defaultValue: string | boolean;
  icon: React.ReactNode;
  iconLabel: string;
  options?: { value: string; label: string; icon: React.ReactNode; disabled?: boolean }[];
}

const clientConfig: AppConfigItem = {
  label: "Is this a client app?",
  tooltip: "Check if this is a client-side application (e.g. React/Vite/Next)",
  defaultValue: false,
  icon: <Monitor className="w-4" />,
  iconLabel: "Client",
};

const portConfig: AppConfigItem = {
  label: "Port",
  tooltip: "The port your application listens on",
  defaultValue: "3000",
  icon: <></>,
  iconLabel: "Port",
};

const githubUrlConfig: AppConfigItem = {
  label: "GitHub URL",
  tooltip: "The GitHub repository URL (e.g., https://github.com/username/repo)",
  defaultValue: "",
  icon: <></>,
  iconLabel: "GitHub URL",
};

export const appConfigs = {
  auth: authConfig,
  providers: providerConfig,
  client: clientConfig,
  port: portConfig,
  githubUrl: githubUrlConfig,
};

export const templates = {
  "next-shadcnui": {
    value: "next-shadcnui",
    icon: <SiNextdotjs className="w-4 text-[#000000] dark:text-white" />,
    label: "NextJS + ShadcnUI",
    type: "web",
  },
  "vite-react-shadcnui": {
    value: "vite-react-shadcnui",
    icon: <SiReact className="w-4 text-[#61DAFB]" />,
    label: "Vite + React + ShadcnUI",
    type: "web",
  },
  tanstack: {
    value: "tanstack",
    icon: <SiReactquery className="w-4 text-[#FF4154]" />,
    label: "TanStack Start",
    type: "web",
    disabled: true,
  },
  sveltekit: {
    value: "sveltekit",
    icon: <SiSvelte className="w-4 text-[#FF3E00]" />,
    label: "SvelteKit",
    type: "web",
    disabled: true,
  },
  nuxt: {
    value: "nuxt",
    icon: <SiNuxtdotjs className="w-4 text-[#00DC82]" />,
    label: "Nuxt.js",
    type: "web",
    disabled: true,
  },
  vue: {
    value: "vue",
    icon: <SiVuedotjs className="w-4 text-[#4FC08D]" />,
    label: "Vue.js",
    type: "web",
    disabled: true,
  },
  angular: {
    value: "angular",
    icon: <SiAngular className="w-4 text-[#DD0031]" />,
    label: "Angular",
    type: "web",
    disabled: true,
  },
  solid: {
    value: "solid",
    icon: <SiSolid className="w-4 text-[#2C4F7C]" />,
    label: "SolidJS",
    type: "web",
    disabled: true,
  },
  qwik: {
    value: "qwik",
    icon: <SiQwik className="w-4 text-[#16B4FB]" />,
    label: "Qwik",
    type: "web",
    disabled: true,
  },
  "astro-react": {
    value: "astro-react",
    icon: <SiAstro className="w-4 text-[#FF5D01]" />,
    label: "Astro + React",
    type: "landing-page",
  },
  framer: {
    value: "framer",
    icon: <SiFramer className="w-4 text-[#000000] dark:text-white" />,
    label: "Framer",
    type: "landing-page",
    disabled: true,
  },
  webflow: {
    value: "webflow",
    icon: <SiWebflow className="w-4 text-[#4353FF]" />,
    label: "Webflow",
    type: "landing-page",
    disabled: true,
  },
  carrd: {
    value: "carrd",
    icon: <SiCarrd className="w-4 text-[#33A9FF]" />,
    label: "Carrd",
    type: "landing-page",
    disabled: true,
  },
  wix: {
    value: "wix",
    icon: <SiWix className="w-4 text-[#000000] dark:text-white" />,
    label: "Wix",
    type: "landing-page",
    disabled: true,
  },
  squarespace: {
    value: "squarespace",
    icon: <SiSquarespace className="w-4 text-[#000000] dark:text-white" />,
    label: "Squarespace",
    type: "landing-page",
    disabled: true,
  },
  tailwindui: {
    value: "tailwindui",
    icon: <SiTailwindcss className="w-4 text-[#06B6D4]" />,
    label: "Tailwind UI",
    type: "landing-page",
    disabled: true,
  },
  reactnative: {
    value: "reactnative",
    icon: <SiReact className="w-4 text-[#61DAFB]" />,
    label: "React Native",
    type: "mobile",
  },
  expo: {
    value: "expo",
    icon: <SiExpo className="w-4 text-[#000020] dark:text-white" />,
    label: "Expo",
    type: "mobile",
    disabled: true,
  },
  flutter: {
    value: "flutter",
    icon: <SiFlutter className="w-4 text-[#02569B]" />,
    label: "Flutter",
    type: "mobile",
    disabled: true,
  },
  swift: {
    value: "swift",
    icon: <SiSwift className="w-4 text-[#F05138]" />,
    label: "Swift (iOS)",
    type: "mobile",
    disabled: true,
  },
  kotlin: {
    value: "kotlin",
    icon: <SiKotlin className="w-4 text-[#7F52FF]" />,
    label: "Kotlin (Android)",
    type: "mobile",
    disabled: true,
  },
  ionic: {
    value: "ionic",
    icon: <SiIonic className="w-4 text-[#3880FF]" />,
    label: "Ionic",
    type: "mobile",
    disabled: true,
  },
  "astro-react-blog": {
    value: "astro-react-blog",
    icon: <SiAstro className="w-4 text-[#FF5D01]" />,
    label: "Astro + React Blog",
    type: "blog",
  },
  ghost: {
    value: "ghost",
    icon: <SiGhost className="w-4 text-[#000000] dark:text-white" />,
    label: "Ghost",
    type: "blog",
    disabled: true,
  },
  hugo: {
    value: "hugo",
    icon: <SiHugo className="w-4 text-[#FF4088]" />,
    label: "Hugo",
    type: "blog",
    disabled: true,
  },
  jekyll: {
    value: "jekyll",
    icon: <SiJekyll className="w-4 text-[#CC0000]" />,
    label: "Jekyll",
    type: "blog",
    disabled: true,
  },
  gatsby: {
    value: "gatsby",
    icon: <SiGatsby className="w-4 text-[#663399]" />,
    label: "Gatsby",
    type: "blog",
    disabled: true,
  },
  docusaurus: {
    value: "docusaurus",
    icon: <SiDocusaurus className="w-4 text-[#3ECC5F]" />,
    label: "Docusaurus",
    type: "blog",
    disabled: true,
  },
  hashnode: {
    value: "hashnode",
    icon: <SiHashnode className="w-4 text-[#2962FF]" />,
    label: "Hashnode",
    type: "blog",
    disabled: true,
  },
  wordpress: {
    value: "wordpress",
    icon: <SiWordpress className="w-4 text-[#21759B]" />,
    label: "WordPress",
    type: "blog",
    disabled: true,
  },
  "electron-react-shadcnui": {
    value: "electron-react-shadcnui",
    icon: <SiReact className="w-4 text-[#61DAFB]" />,
    label: "Electron - ReactJS + ShadcnUI",
    type: "desktop",
  },
  tauri: {
    value: "tauri",
    icon: <SiTauri className="w-4 text-[#24C8DB]" />,
    label: "Tauri",
    type: "desktop",
    disabled: true,
  },
  wails: {
    value: "wails",
    icon: <SiWails className="w-4 text-[#E33831]" />,
    label: "Wails",
    type: "desktop",
    disabled: true,
  },
  neutralinojs: {
    value: "neutralinojs",
    icon: <SiNeutralinojs className="w-4 text-[#F7DF1E]" />,
    label: "Neutralinojs",
    type: "desktop",
    disabled: true,
  },
  flutter_desktop: {
    value: "flutter_desktop",
    icon: <SiFlutter className="w-4 text-[#02569B]" />,
    label: "Flutter (Desktop)",
    type: "desktop",
    disabled: true,
  },
  qt: {
    value: "qt",
    icon: <SiQt className="w-4 text-[#41CD52]" />,
    label: "Qt",
    type: "desktop",
    disabled: true,
  },
  gtk: {
    value: "gtk",
    icon: <SiGtk className="w-4 text-[#7FE719]" />,
    label: "GTK",
    type: "desktop",
    disabled: true,
  },
  "node-hono-postgres": {
    value: "node-hono-postgres",
    icon: <RiNodejsLine className="w-4 text-[#539E43]" />,
    label: "Hono + Postgres Adapter",
    type: "service",
  },
  "node-hono": {
    value: "node-hono",
    icon: <RiNodejsLine className="w-4 text-[#539E43]" />,
    label: "Hono",
    type: "service",
  },
  postgres: {
    value: "postgres",
    icon: <BiLogoPostgresql className="w-4 text-[#4169E1]" />,
    label: "Postgres",
    type: "database",
  },
  mysql: {
    value: "mysql",
    icon: <SiMysql className="w-4 text-[#4479A1]" />,
    label: "MySQL",
    type: "database",
  },
  mongodb: {
    value: "mongodb",
    icon: <SiMongodb className="w-4 text-[#47A248]" />,
    label: "MongoDB",
    type: "database",
  },
  redis: {
    value: "redis",
    icon: <SiRedis className="w-4 text-[#FF4438]" />,
    label: "Redis",
    type: "database",
    disabled: true,
  },
  sqlite: {
    value: "sqlite",
    icon: <SiSqlite className="w-4 text-[#003B57]" />,
    label: "SQLite",
    type: "database",
    disabled: true,
  },
  mariadb: {
    value: "mariadb",
    icon: <SiMariadb className="w-4 text-[#003545]" />,
    label: "MariaDB",
    type: "database",
    disabled: true,
  },
  surrealdb: {
    value: "surrealdb",
    icon: <SiSurrealdb className="w-4 text-[#FF00A0]" />,
    label: "SurrealDB",
    type: "database",
    disabled: true,
  },
  duckdb: {
    value: "duckdb",
    icon: <SiDuckdb className="w-4 text-[#FFF000]" />,
    label: "DuckDB",
    type: "database",
    disabled: true,
  },
  supabase: {
    value: "supabase",
    icon: <SiSupabase className="w-4 text-[#3ECF8E]" />,
    label: "Supabase",
    type: "database",
    disabled: true,
  },
  "worker-cloudflare": {
    value: "worker-cloudflare",
    icon: <SiCloudflare className="w-4 text-[#F38020]" />,
    label: "Cloudflare",
    type: "worker",
  },
  "node-hono-lucia-postgres": {
    value: "node-hono-lucia-postgres",
    icon: <RiNodejsLine className="w-4 text-[#539E43]" />,
    label: "Hono + Arctic + Postgres Adapter",
    type: "auth-service",
  },
  clerk: {
    value: "clerk",
    icon: <SiClerk className="w-4 text-[#6C47FF]" />,
    label: "Clerk",
    type: "auth-service",
    disabled: true,
  },
  auth0: {
    value: "auth0",
    icon: <SiAuth0 className="w-4 text-[#EB5424]" />,
    label: "Auth0",
    type: "auth-service",
    disabled: true,
  },
  firebase_auth: {
    value: "firebase_auth",
    icon: <SiFirebase className="w-4 text-[#FFCA28]" />,
    label: "Firebase Auth",
    type: "auth-service",
    disabled: true,
  },
  supertokens: {
    value: "supertokens",
    icon: <LockKeyhole className="w-4" />,
    label: "SuperTokens",
    type: "auth-service",
    disabled: true,
  },
  keycloak: {
    value: "keycloak",
    icon: <SiKeycloak className="w-4 text-[#0085CF]" />,
    label: "Keycloak",
    type: "auth-service",
    disabled: true,
  },
  pocketbase_auth: {
    value: "pocketbase_auth",
    icon: <SiPocketbase className="w-4 text-[#B8DBE4]" />,
    label: "Pocketbase Auth",
    type: "auth-service",
    disabled: true,
  },
  okta: {
    value: "okta",
    icon: <SiOkta className="w-4 text-[#007DC1]" />,
    label: "Okta",
    type: "auth-service",
    disabled: true,
  },
  express: {
    value: "express",
    icon: <RiNodejsLine className="w-4 text-[#539E43]" />,
    label: "Express",
    type: "service",
    disabled: true,
  },
  nestjs: {
    value: "nestjs",
    icon: <RiNodejsLine className="w-4 text-[#539E43]" />,
    label: "NestJS",
    type: "service",
    disabled: true,
  },
  fastify: {
    value: "fastify",
    icon: <RiNodejsLine className="w-4 text-[#539E43]" />,
    label: "Fastify",
    type: "service",
    disabled: true,
  },
  trpc: {
    value: "trpc",
    icon: <RiNodejsLine className="w-4 text-[#539E43]" />,
    label: "tRPC + Express",
    type: "service",
    disabled: true,
  },
  fastapi: {
    value: "fastapi",
    icon: <SiPython className="w-4 text-[#3776AB]" />,
    label: "FastAPI",
    type: "service",
    disabled: true,
  },
  django: {
    value: "django",
    icon: <SiPython className="w-4 text-[#3776AB]" />,
    label: "Django",
    type: "service",
    disabled: true,
  },
  flask: {
    value: "flask",
    icon: <SiPython className="w-4 text-[#3776AB]" />,
    label: "Flask",
    type: "service",
    disabled: true,
  },
  "go-gin": {
    value: "go-gin",
    icon: <SiGo className="w-4 text-[#00ADD8]" />,
    label: "Gin",
    type: "service",
    disabled: true,
  },
  "rust-axum": {
    value: "rust-axum",
    icon: <SiRust className="w-4 text-[#DEA584]" />,
    label: "Axum",
    type: "service",
    disabled: true,
  },
  "bun-elysia": {
    value: "bun-elysia",
    icon: <SiBun className="w-4 text-[#fbf0df]" />,
    label: "Elysia",
    type: "service",
    disabled: true,
  },
  "github-repo": {
    value: "github-repo",
    icon: <SiGithub className="w-4 text-[#181717] dark:text-white" />,
    label: "Github Repo",
    type: "custom-repo",
  },
  "github-url": {
    value: "github-url",
    icon: <SiGithub className="w-4 text-[#181717] dark:text-white" />,
    label: "Github URL",
    type: "custom-url",
  },
  // "openclaw": {
  //   value: "openclaw",
  //   icon: <Bot className="w-4 text-[#ff4d4d]" />,
  //   label: "OpenClaw AI",
  //   type: "openclaw",
  // },
};

export const templateKeys = Object.keys(templates) as [
  keyof typeof templates,
  ...(keyof typeof templates)[],
];

export const templateTypeMapping: Record<string, string> = Object.entries(
  templates,
).reduce(
  (acc, [key, val]) => {
    acc[key] = val.type;
    return acc;
  },
  {} as Record<string, string>,
);

interface checkboxOption {
  db?: AppConfigItem;
  auth?: AppConfigItem;
  provider?: AppConfigItem;
  client?: AppConfigItem;
}

interface comboboxOption {
  provider?: AppConfigItem;
}

interface inputOption {
  port?: AppConfigItem;
  githubUrl?: AppConfigItem;
}

type NodesType = {
  [key: string]:
    | {
        icon?: React.ReactNode;
        templates?: (typeof templates)["next-shadcnui"][];
        checkboxOptions?: checkboxOption;
        comboboxOptions?: comboboxOption;
        inputOptions?: inputOption;
        disabled?: boolean;
      }
    | undefined;
};

export const nodes: NodesType = {
  openclaw: {
    icon: <Bot className="w-4 text-[#ff4d4d]" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "openclaw")
      .map(([_, template]) => template),
  },
  mobile: {
    icon: <TabletSmartphone className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "mobile")
      .map(([_, template]) => template),
    checkboxOptions: {
      auth: authConfig,
    },
  },
  web: {
    icon: <Chrome className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "web")
      .map(([_, template]) => template),
    checkboxOptions: {
      auth: authConfig,
    },
  },
  desktop: {
    icon: <MonitorCheck className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "desktop")
      .map(([_, template]) => template),
    checkboxOptions: {
      auth: authConfig,
    },
  },
  service: {
    icon: <ChevronsLeftRightEllipsis className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "service")
      .map(([_, template]) => template),
    checkboxOptions: {
      // db: dbConfig,
      // Decided to remove the db config as it is not adding any value currently.
    },
  },
  database: {
    icon: <Database className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "database")
      .map(([_, template]) => template),
  },
  "landing-page": {
    icon: <Chrome className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "landing-page")
      .map(([_, template]) => template),
  },
  blog: {
    icon: <Chrome className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "blog")
      .map(([_, template]) => template),
  },
  "auth-service": {
    icon: <LockKeyhole className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "auth-service")
      .map(([_, template]) => template),
    comboboxOptions: {
      provider: providerConfig,
    },
  },
  llm: {
    icon: <Sparkles className="w-4 text-blue-500" />,
    disabled: true,
  },
  worker: {
    icon: <UserRoundCheck className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "worker")
      .map(([_, template]) => template),
  },


  cache: {
    icon: <HardDrive className="w-4 text-blue-500" />,
    disabled: true,
  },
  "object-storage": {
    icon: <Container className="w-4 text-blue-500" />,
    disabled: true,
  },
  "message-queue": {
    icon: <MessageSquareShare className="w-4 text-blue-500" />,
    disabled: true,
  },
  monitoring: {
    icon: <Activity className="w-4 text-blue-500" />,
    disabled: true,
  },
  "managed-db": {
    icon: <DatabaseBackup className="w-4 text-blue-500" />,
    disabled: true,
  },
  "custom-repo": {
    icon: <SiGithub className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "custom-repo")
      .map(([_, template]) => template),
    checkboxOptions: {
      client: clientConfig,
    },
    inputOptions: {
      port: portConfig,
    },
  },
  "custom-url": {
    icon: <SiGithub className="w-4 text-blue-500" />,
    templates: Object.entries(templates)
      .filter(([_, template]) => template.type === "custom-url")
      .map(([_, template]) => template),
    checkboxOptions: {
      client: clientConfig,
    },
    inputOptions: {
      port: portConfig,
    },
  },
};

export type NodeType = keyof typeof nodes;

export const nodeTypes = Object.entries(nodes)
  .filter(([_, value]) => !value?.disabled)
  .map(([key]) => key as string) as [string, ...string[]];
